import axios from "axios";
import { getToken } from "./userService";
import { getCurrentUserId } from "./dashboardService";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://backend-dot-immersely-ai.nw.r.appspot.com/api/v1";
// Use production backend for web-auth endpoint
const WEB_AUTH_API_URL = API_BASE_URL;

function hasEnteredAccessToken(accessToken) {
  return typeof accessToken === "string" && accessToken.trim().length > 0;
}

function hasEnteredAccessCredentials(accessCredentials) {
  if (accessCredentials == null || accessCredentials === "") {
    return false;
  }

  let parsed = accessCredentials;
  if (typeof accessCredentials === "string") {
    const trimmed = accessCredentials.trim();
    if (!trimmed) {
      return false;
    }
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return true;
    }
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return false;
  }

  return Object.keys(parsed).length > 0;
}

/**
 * Partner auth is only required when the developer entered accessToken
 * or accessCredentials for the game.
 */
export function assignmentRequiresAuth(assignment) {
  if (typeof assignment?.requiresAuth === "boolean") {
    return assignment.requiresAuth;
  }

  return (
    hasEnteredAccessToken(assignment?.accessToken) ||
    hasEnteredAccessCredentials(assignment?.accessCredentials)
  );
}

/**
 * Authenticate with game partner (e.g., BeGenio) via backend proxy
 * Uses per-game configuration from games.accessCredentials
 * @param {number} gameId - The game ID (required)
 * @param {number} userId - The user ID (optional, will use current user if not provided)
 * @param {string} customId - Custom ID for partner (optional, defaults to userId)
 * @param {string} displayName - Display name for partner (optional)
 * @returns {Promise<Object>} Object with sessionTicket and isNewAccount
 */
export async function authenticateWithGamePartner(gameId, userId = null, customId = null, displayName = null) {
  if (!gameId) {
    throw new Error("Game ID is required for authentication");
  }

  try {
    // Get current user ID if not provided
    if (!userId) {
      userId = getCurrentUserId();
    }

    // Get user profile for display name if not provided
    if (!displayName) {
      const userData = localStorage.getItem("userData");
      if (userData) {
        try {
          const parsed = JSON.parse(userData);
          displayName = parsed.name || `User${userId}`;
        } catch (e) {
          displayName = `User${userId}`;
        }
      } else {
        displayName = `User${userId}`;
      }
    }

    // Use customId or default to userId
    const immerselyUserId = customId || `immerselyUser${userId}`;

    // Call backend proxy endpoint: POST /games/:gameId/web-auth
    // Using localhost:8080 for debugging
    // Backend will handle BeGenio token generation/retrieval
    const response = await axios.post(
      `${WEB_AUTH_API_URL}/games/${gameId}/web-auth`,
      {
        partnerId: "immersely",
        customId: immerselyUserId,
        displayName: displayName,
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`, // Immersely token for backend auth
        },
      }
    );

    // Backend proxy returns the partner's response (sessionTicket, etc.)
    return response.data;
  } catch (error) {
    // Handle specific error cases
    if (error.response?.status === 400) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || "Authentication failed";
      if (errorMessage.includes("not configured") || errorMessage.includes("Auth is not configured")) {
        throw new Error(
          "Authentication is not configured for this game. " +
          "Please contact support or try again later."
        );
      }
      throw new Error(errorMessage);
    }
    
    if (error.response?.status === 401) {
      throw new Error("Unauthorized. Please log in again.");
    }

    if (error.response?.status === 404) {
      throw new Error("Game authentication endpoint not found. Please contact support.");
    }

    if (error.response?.status >= 500) {
      throw new Error("Authentication service error. Please try again later.");
    }

    // Network or other errors
    throw new Error(error.message || "Failed to authenticate. Please try again.");
  }
}

/**
 * Build game URL with session ticket
 * @param {string} webUrl - Base web URL for the game
 * @param {string} sessionTicket - Session ticket from partner
 * @returns {string} Game URL with session ticket query parameter
 */
export function buildGameUrl(webUrl, sessionTicket) {
  if (!webUrl) {
    throw new Error("Web URL is required");
  }
  if (!sessionTicket) {
    throw new Error("Session ticket is required");
  }

  const url = new URL(webUrl);
  url.searchParams.set("sessionTicket", sessionTicket);
  return url.toString();
}

/**
 * Launch a web game. Partner authentication runs only when the developer
 * entered accessToken or accessCredentials for the game.
 * @param {Object} assignment - Assignment object with game details
 * @returns {Promise<Object>} Object with gameUrl and sessionTicket
 */
export async function launchGame(assignment) {
  try {
    if (!assignment || !assignment.gameId) {
      throw new Error("Invalid assignment: gameId is required");
    }

    if (assignment.buildType !== "web") {
      throw new Error("Only web games can be launched through this service");
    }

    if (!assignment.webUrl) {
      throw new Error("Web URL is required for web games");
    }

    if (!assignmentRequiresAuth(assignment)) {
      return {
        gameUrl: assignment.webUrl,
        sessionTicket: null,
        isNewAccount: false,
      };
    }

    // Authenticate via backend proxy (uses per-game config)
    const authResult = await authenticateWithGamePartner(
      assignment.gameId,
      null, // userId - will be fetched automatically
      null, // customId - will default to immerselyUser{userId}
      null  // displayName - will be fetched from userData
    );

    // Extract sessionTicket from response (may be named differently by partner)
    const sessionTicket = authResult.sessionTicket || authResult.ticket || authResult.authToken;
    
    if (!sessionTicket) {
      throw new Error("No session ticket received from authentication service");
    }

    // Build game URL with session ticket
    const gameUrl = buildGameUrl(assignment.webUrl, sessionTicket);

    return {
      gameUrl,
      sessionTicket: sessionTicket,
      isNewAccount: authResult.isNewAccount || false,
    };
  } catch (error) {
    throw error;
  }
}

/**
 * Check whether a web game URL allows iframe embedding on Immersely.
 * @param {string} url
 * @returns {Promise<{ canEmbed: boolean, reason?: string|null }>}
 */
export async function checkGameEmbeddable(url) {
  if (!url) {
    return { canEmbed: true, reason: "missing_url" };
  }

  try {
    const response = await axios.get(`${API_BASE_URL}/games/embed-check`, {
      params: { url },
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    });
    return {
      canEmbed: Boolean(response.data?.canEmbed),
      reason: response.data?.reason ?? null,
      checkedUrl: response.data?.checkedUrl || url,
    };
  } catch (error) {
    // Fail open — GameLauncher still offers a manual external fallback.
    console.warn("Embed check failed, defaulting to iframe launch:", error);
    return { canEmbed: true, reason: "embed_check_request_failed" };
  }
}
